// Top-of-page banner that warns an editor when their browser is in
// "preview mode" — either a stuck phase override (?phase=X) or the
// feature-flag bypass (?preview=1). Both are sticky in localStorage,
// both have caused production-readiness confusion during the build
// (someone testing a phase forgets they overrode it, then thinks the
// real phase isn't switching).
//
// Renders NOTHING when neither override is set — so public readers
// never see it. The "Clear" button wipes both localStorage keys and
// reloads the page so the editor sees what readers see, in one click.

import { useState, useEffect } from 'react';
import {
  useEffectivePhase,
  clearPhaseOverride,
  PHASE_OVERRIDE_KEY,
} from '../lib/useEffectivePhase';
import {
  usePreviewBypass,
  clearPreviewBypass,
  PREVIEW_BYPASS_KEY,
} from '../lib/usePreviewBypass';

export default function PreviewBanner() {
  const { phase, isOverride, dbPhase } = useEffectivePhase();
  const bypass = usePreviewBypass();

  // Recompute on storage events so clearing in one tab updates others.
  // (Same browser, same origin — localStorage events broadcast.)
  const [tick, setTick] = useState(0);
  useEffect(() => {
    function onStorage(e: StorageEvent) {
      if (e.key === PHASE_OVERRIDE_KEY || e.key === PREVIEW_BYPASS_KEY) {
        setTick((t) => t + 1);
      }
    }
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);
  void tick;

  if (!isOverride && !bypass) return null;

  function clearAll() {
    clearPhaseOverride();
    clearPreviewBypass();
    // Strip any ?phase= / ?preview= from the URL so reload doesn't
    // immediately re-apply the override we just cleared.
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete('phase');
      url.searchParams.delete('preview');
      window.location.href = url.toString();
    } catch {
      window.location.reload();
    }
  }

  return (
    <div style={bannerStyle} role="status" aria-live="polite">
      <div style={innerStyle}>
        <span style={iconStyle} aria-hidden="true">
          ⚠
        </span>
        <span style={labelStyle}>Editor preview mode active</span>
        <span style={detailStyle}>
          {isOverride && (
            <>
              <strong>Phase:</strong> showing <code style={codeStyle}>{phase}</code>
              {' '}— DB is set to <code style={codeStyle}>{dbPhase}</code>.{' '}
            </>
          )}
          {bypass && (
            <>
              <strong>Flags:</strong> all hidden/coming-soon flags forced to
              live for this browser.{' '}
            </>
          )}
          Public readers see the real values — this only affects this
          browser.
        </span>
        <button type="button" onClick={clearAll} style={buttonStyle}>
          Clear &amp; reload
        </button>
      </div>
    </div>
  );
}

// Inline styles so the banner has zero CSS dependencies — it must
// render BEFORE the rest of the app loads, even if global.css is
// somehow late.
const bannerStyle: React.CSSProperties = {
  position: 'sticky',
  top: 0,
  zIndex: 9999,
  background: '#fff8d6',
  borderBottom: '2px solid #c89200',
  color: '#3a2a00',
  fontFamily:
    "var(--font-display, 'Poppins', system-ui, -apple-system, sans-serif)",
  fontSize: 13,
  lineHeight: 1.4,
};

const innerStyle: React.CSSProperties = {
  maxWidth: 1500,
  margin: '0 auto',
  padding: '8px 16px',
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  flexWrap: 'wrap',
};

const iconStyle: React.CSSProperties = {
  fontSize: 16,
  fontWeight: 900,
};

const labelStyle: React.CSSProperties = {
  fontWeight: 800,
  textTransform: 'uppercase',
  letterSpacing: 1,
  fontSize: 11,
};

const detailStyle: React.CSSProperties = {
  flex: '1 1 200px',
  minWidth: 0,
};

const codeStyle: React.CSSProperties = {
  background: 'rgba(0,0,0,0.08)',
  padding: '1px 5px',
  borderRadius: 3,
  fontFamily: "'JetBrains Mono', 'Menlo', monospace",
  fontSize: 12,
};

const buttonStyle: React.CSSProperties = {
  appearance: 'none',
  border: '1.5px solid #3a2a00',
  background: '#3a2a00',
  color: '#fff8d6',
  padding: '6px 12px',
  borderRadius: 4,
  fontFamily:
    "var(--font-display, 'Poppins', system-ui, sans-serif)",
  fontWeight: 800,
  fontSize: 11,
  letterSpacing: 0.6,
  textTransform: 'uppercase',
  cursor: 'pointer',
  flexShrink: 0,
};
